/**
 * ============================================================================
 * ProgramSelectorBanner — Controller (client)
 * ============================================================================
 * Owns wizard (quiz → lead → results), RHF form state, local OTP
 * (/api/otp/send + verify — NOT OTPContext), skip for 23* / international,
 * leadSquareApiCallFlow / BothFlow, GA side effects.
 * Paint-first busy on form submit + proceed. No JSX.
 * ============================================================================
 */
"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type ClipboardEvent as ReactClipboardEvent,
} from "react";
import { useForm, useWatch, type Control } from "react-hook-form";
import { fetchIpInfoData } from "@/context/ip.storage.utils";
import { getGlobalStateValue } from "@/context/utils";
import { beginSubmitInteraction } from "@/helpers/paintFirst";
import { pushToDataLayer } from "@/helpers/gtag";
import { setLeadFormInputValue } from "@/helpers/leadFormDegreeProgramFields";
import {
  DEFAULT_FORM_VALUES,
  NATIONAL_CODE,
  QUESTIONS,
  RESEND_SECONDS,
  REGEX,
  createProgramSelectorFormResolver,
  shouldSkipOtpForMobile,
  type ProgramSelectorBannerProps,
  type ProgramSelectorFormValues,
  type ProgramSelectorStep,
  type Question,
  type QuestionOption,
} from "../model/programSelectorBanner.types";
import {
  proceedProgramSelectorLead,
  sendLocalOtp,
  submitProgramSelectorLead,
  verifyLocalOtp,
} from "../service/programSelectorBanner.service";

const IP_INFO_DELAY_MS = 1000;

export type UseProgramSelectorBannerControllerOptions =
  ProgramSelectorBannerProps;

export interface ProgramSelectorBannerViewModel {
  isOpen: boolean;
  step: ProgramSelectorStep;
  questionIndex: number;
  answers: Record<string, string>;
  programs: any[];
  certificate: any[];
  // react-hook-form bindings — view binds fields via <Controller control={control} />.
  control: Control<ProgramSelectorFormValues>;
  errors: Partial<Record<keyof ProgramSelectorFormValues, string | undefined>>;
  otp: string[];
  showOtp: boolean;
  otpResponse: { hash?: string; mobile?: string } | null;
  otpError: string;
  isSendingOtp: boolean;
  resendCooldown: number;
  isSubmittingForm: boolean;
  isProceeding: boolean;
  proceedError: string;
  selectedProgramIndex: number;
  selectedCertIndex: number;
  totalQuestions: number;
  currentQuestion: Question;
  currentOptions: QuestionOption[];
  questionsProgressPct: number;
  isInternational: boolean;
  isFormValid: boolean;
  canSubmitForm: boolean;
  canProceed: boolean;
  isNational: boolean;
  otpDisplayCountryCode: string;
  otpDisplayMobile: string;
  inputsRef: MutableRefObject<(HTMLInputElement | null)[]>;
  openModal: () => void;
  closeModal: () => void;
  handleSelectAnswer: (optionId: string) => void;
  handleGoBack: () => void;
  handleFieldChange: (
    field: keyof ProgramSelectorFormValues,
    value: string | boolean,
  ) => void;
  handleFieldBlur: (field: "fullName" | "email" | "mobile") => void;
  updateCountryCode: (value: string) => void;
  handleValidate: (e?: ReactMouseEvent) => Promise<void>;
  handleEditNumber: () => void;
  handleResendOtp: () => Promise<void>;
  formatCooldown: (s: number) => string;
  handleFormSubmit: (e: FormEvent) => Promise<void>;
  selectProgram: (program: any, index: number) => void;
  selectCertificate: (cert: any, index: number) => void;
  handleProceed: (e?: ReactMouseEvent) => Promise<void>;
  handleSetOtp: (value: string, index: number) => void;
  handleOtpKeyDown: (e: ReactKeyboardEvent, index: number) => void;
  handleOtpPaste: (e: ReactClipboardEvent) => void;
}

export function useProgramSelectorBannerController(
  options: UseProgramSelectorBannerControllerOptions,
): ProgramSelectorBannerViewModel {
  const {
    data = [],
    associatedPrograms = [],
    associatedCertification = [],
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<ProgramSelectorStep>("questions");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [programs, setPrograms] = useState<any[]>([]);
  const [certificate, setCertificate] = useState<any[]>([]);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [showOtp, setShowOtp] = useState(false);
  const [otpResponse, setOtpResponse] = useState<{
    hash?: string;
    mobile?: string;
  } | null>(null);
  const [otpError, setOtpError] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isSubmitUIBusy, setIsSubmitUIBusy] = useState(false);
  const [leadResponse, setLeadResponse] = useState<any>(null);
  const [isProceeding, setIsProceeding] = useState(false);
  const [proceedError, setProceedError] = useState("");
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [selectedProgramIndex, setSelectedProgramIndex] = useState(-1);
  const [selectedCertIndex, setSelectedCertIndex] = useState(-1);
  const [selectedDegreeProgramCode, setSelectedDegreeProgramCode] =
    useState("");
  const [selectedSpecProgramCode, setSelectedSpecProgramCode] = useState("");
  const [selectedCertProgramCode, setSelectedCertProgramCode] = useState("");
  const isFiredOnceIpinfo = useRef(false);

  const resolver = useMemo(() => createProgramSelectorFormResolver(), []);

  const {
    control,
    setValue,
    getValues,
    reset,
    trigger,
    handleSubmit: rhfHandleSubmit,
    formState: { errors: rhfErrors, isSubmitting },
  } = useForm<ProgramSelectorFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onChange",
    reValidateMode: "onChange",
    resolver,
  });

  // Narrow useWatch subscriptions for side-effect / validity effects only.
  // Field values in the view come from per-field <Controller> render props.
  const watchedFullName = useWatch({ control, name: "fullName" });
  const watchedEmail = useWatch({ control, name: "email" });
  const watchedMobile = useWatch({ control, name: "mobile" });
  const watchedCountryCode = useWatch({ control, name: "countryCode" });
  const watchedConsent = useWatch({ control, name: "consent" });

  const isSubmittingForm = isSubmitUIBusy || isSubmitting;

  const errors: ProgramSelectorBannerViewModel["errors"] = {
    fullName: rhfErrors.fullName?.message,
    mobile: rhfErrors.mobile?.message,
    email: rhfErrors.email?.message,
    consent: rhfErrors.consent?.message,
  };

  const selectedMappings = useMemo(() => {
    if (
      !answers.EducationLevel ||
      !answers.InterestIndustry ||
      !answers.MainGoal
    ) {
      return [];
    }
    return (data || []).filter(
      (d: any) =>
        d.EducationLevel === answers.EducationLevel &&
        d.InterestIndustry === answers.InterestIndustry &&
        d.MainGoal === answers.MainGoal,
    );
  }, [data, answers]);

  const associatedSlugs = useMemo(() => {
    const programSlugs = new Set<string>();
    const certificationSlugs = new Set<string>();

    selectedMappings.forEach((item: any) => {
      JSON.parse(item.associatedPrograms || "[]").forEach((p: any) =>
        programSlugs.add(p.value),
      );
      JSON.parse(item.associatedSpecializations || "[]").forEach((s: any) =>
        programSlugs.add(s.value),
      );
      JSON.parse(item.associatedCertifications || "[]").forEach((c: any) =>
        certificationSlugs.add(c.value),
      );
      JSON.parse(item.associatedCertificationsSOAI || "[]").forEach((c: any) =>
        certificationSlugs.add(c.value),
      );
    });

    return {
      programs: Array.from(programSlugs),
      certifications: Array.from(certificationSlugs),
    };
  }, [selectedMappings]);

  useEffect(() => {
    const program = (associatedPrograms || []).filter((a: any) => {
      return associatedSlugs.programs.includes(
        a.attributes.BasicDetails.url_key.replace(/^\//, ""),
      );
    });

    const normalizedCertifications = associatedSlugs.certifications.map(
      (item) => item.replace(/\/$/, "").split("/").pop(),
    );

    const certs = (associatedCertification || []).filter((a: any) => {
      const slug = a.attributes.BasicDetails.url_key
        .replace(/\/$/, "")
        .split("/")
        .pop();
      return normalizedCertifications.includes(slug);
    });

    setPrograms(program);
    setCertificate(certs);
  }, [associatedSlugs, associatedPrograms, associatedCertification]);

  const totalQuestions = QUESTIONS.length;
  const currentQuestion = QUESTIONS[questionIndex];
  const currentOptions =
    currentQuestion?.id === "InterestIndustry" &&
    answers?.EducationLevel === "PG"
      ? currentQuestion.options.filter((opt) => opt?.id !== "Travel_and_Tourism")
      : currentQuestion?.options || [];
  const questionsProgressPct = Math.round(
    ((questionIndex + (answers[currentQuestion?.id] ? 1 : 0)) / totalQuestions) *
      100,
  );

  const isInternational = watchedCountryCode !== NATIONAL_CODE;
  const shouldSkipOtp = shouldSkipOtpForMobile(watchedMobile);
  const isOtpComplete = otp.every((d) => d !== "");
  const isFormValid =
    REGEX.fullName.test(String(watchedFullName || "").trim()) &&
    REGEX.email.test(String(watchedEmail || "").trim()) &&
    (watchedCountryCode === NATIONAL_CODE
      ? REGEX.mobileIndia.test(String(watchedMobile || "").trim())
      : REGEX.mobileIntl.test(String(watchedMobile || "").trim())) &&
    watchedConsent === true;
  const canSubmitForm =
    isFormValid &&
    (isInternational || (showOtp && isOtpComplete)) &&
    !isSubmittingForm;
  const isNational = watchedCountryCode === NATIONAL_CODE;
  const isProgramSelected = selectedProgramIndex >= 0;
  const isCertSelected = selectedCertIndex >= 0;
  const canProceed = isProgramSelected && !isProceeding;

  const resetWizardState = useCallback(() => {
    setStep("questions");
    setQuestionIndex(0);
    setAnswers({});
    reset(DEFAULT_FORM_VALUES);
    setOtp(["", "", "", "", "", ""]);
    setShowOtp(false);
    setOtpResponse(null);
    setOtpError("");
    setIsSendingOtp(false);
    setResendCooldown(0);
    setIsSubmitUIBusy(false);
    setLeadResponse(null);
    setIsProceeding(false);
    setProceedError("");
    setSelectedProgramIndex(-1);
    setSelectedCertIndex(-1);
    setSelectedDegreeProgramCode("");
    setSelectedCertProgramCode("");
    isFiredOnceIpinfo.current = false;
  }, [reset]);

  const openModal = useCallback(() => {
    setIsOpen(true);
    resetWizardState();
    pushToDataLayer({
      event: "lead_initiate",
      page_type: "Home Page",
      cta_text: "Find Your Program",
      page_url_custom: window.location.href,
      section_name: "middle_banner",
    });
  }, [resetWizardState]);

  const closeModal = useCallback(() => setIsOpen(false), []);

  const handleSelectAnswer = useCallback(
    (optionId: string) => {
      let dataLayer: Record<string, any> = {};
      switch (currentQuestion.id) {
        case "EducationLevel":
          dataLayer = {
            event: "first_step",
            page_type: "Home Page",
            cta_text: "Find Your Program",
            page_url_custom: window.location.href,
            section_name: currentQuestion.options.find(
              (id) => id.id === optionId,
            )?.label,
          };
          break;
        case "InterestIndustry":
          dataLayer = {
            event: "second_step",
            page_type: "Home Page",
            cta_text: "Find Your Program",
            page_url_custom: window.location.href,
            section_name: currentQuestion.options.find(
              (id) => id.id === optionId,
            )?.label,
          };
          break;
        default:
          dataLayer = {
            event: "third_step",
            page_type: "Home Page",
            cta_text: "Find Your Program",
            page_url_custom: window.location.href,
            section_name: currentQuestion.options.find(
              (id) => id.id === optionId,
            )?.label,
          };
      }
      const next = { ...answers, [currentQuestion.id]: optionId };
      if (
        currentQuestion.id === "EducationLevel" &&
        optionId === "PG" &&
        next.InterestIndustry === "Travel_and_Tourism"
      ) {
        delete next.InterestIndustry;
      }
      setAnswers(next);
      setTimeout(() => {
        pushToDataLayer(dataLayer);
        if (questionIndex < totalQuestions - 1) {
          setQuestionIndex(questionIndex + 1);
        } else {
          setStep("form");
        }
      }, 200);
    },
    [answers, currentQuestion, questionIndex, totalQuestions],
  );

  const handleGoBack = useCallback(() => {
    if (step === "results") {
      setStep("form");
    } else if (step === "form") {
      setStep("questions");
      setQuestionIndex(totalQuestions - 1);
    } else if (step === "questions" && questionIndex > 0) {
      setQuestionIndex(questionIndex - 1);
    }
  }, [step, questionIndex, totalQuestions]);

  const clearOtpUi = useCallback(() => {
    setShowOtp(false);
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    setOtpResponse(null);
  }, []);

  const handleFieldChange = useCallback(
    (field: keyof ProgramSelectorFormValues, value: string | boolean) => {
      setLeadFormInputValue(setValue, trigger, field, value as never);
      if (field === "countryCode") {
        clearOtpUi();
      }
    },
    [clearOtpUi, setValue, trigger],
  );

  const handleFieldBlur = useCallback(
    (field: "fullName" | "email" | "mobile") => {
      void trigger(field);
      const values = getValues();
      const fieldNameMap = {
        fullName: "FullName",
        email: "email",
        mobile: "phone",
      } as const;
      pushToDataLayer({
        event: "form_filled",
        field_name: fieldNameMap[field],
        option_selected:
          (field === "mobile"
            ? values.mobile
            : field === "email"
              ? values.email
              : values.fullName) || "NA",
        error_message: "NA",
        section_name: getGlobalStateValue("parentSection") || "NA",
        page_type: getGlobalStateValue("pageType"),
        page_url: window?.location?.href || "NA",
      });
    },
    [getValues, trigger],
  );

  const updateCountryCode = useCallback(
    (value: string) => {
      setValue("countryCode", value, { shouldValidate: true, shouldDirty: true });
      setValue("mobile", "", { shouldDirty: true, shouldValidate: true });
      clearOtpUi();
    },
    [clearOtpUi, setValue],
  );

  const handleValidate = useCallback(
    async (e?: ReactMouseEvent) => {
      e?.preventDefault();
      if (!isFormValid || isSendingOtp) return;
      setOtpError("");

      if (shouldSkipOtp) {
        setShowOtp(true);
        setOtp(["", "", "", "", "", ""]);
        setResendCooldown(RESEND_SECONDS);
        return;
      }

      const mobile = getValues("mobile");
      setIsSendingOtp(true);
      try {
        const result = await sendLocalOtp(mobile);
        if (result.success) {
          setOtpResponse({ hash: result.hash, mobile });
          setShowOtp(true);
          setOtp(["", "", "", "", "", ""]);
          setResendCooldown(RESEND_SECONDS);
        } else {
          setOtpError(result.message || "Failed to send OTP. Please try again.");
        }
      } catch {
        setOtpError("Failed to send OTP. Please try again.");
      } finally {
        setIsSendingOtp(false);
      }
    },
    [getValues, isFormValid, isSendingOtp, shouldSkipOtp],
  );

  const handleEditNumber = useCallback(() => {
    clearOtpUi();
    setResendCooldown(0);
  }, [clearOtpUi]);

  const handleResendOtp = useCallback(async () => {
    if (resendCooldown > 0 || isSendingOtp) return;
    setOtpError("");
    setOtp(["", "", "", "", "", ""]);

    if (shouldSkipOtp) {
      setResendCooldown(RESEND_SECONDS);
      return;
    }

    const mobile = getValues("mobile");
    setIsSendingOtp(true);
    try {
      const result = await sendLocalOtp(mobile);
      if (result.success) {
        setOtpResponse({ hash: result.hash, mobile });
        setResendCooldown(RESEND_SECONDS);
      } else {
        setOtpError(
          result.message || "Failed to resend OTP. Please try again.",
        );
      }
    } catch {
      setOtpError("Failed to resend OTP. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  }, [getValues, isSendingOtp, resendCooldown, shouldSkipOtp]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => {
      setResendCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const formatCooldown = useCallback((s: number) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  }, []);

  useEffect(() => {
    if (
      (watchedFullName || watchedEmail || watchedMobile) &&
      !isFiredOnceIpinfo.current
    ) {
      setTimeout(fetchIpInfoData, IP_INFO_DELAY_MS);
      isFiredOnceIpinfo.current = true;
    }
  }, [watchedFullName, watchedEmail, watchedMobile]);

  const buildBaseForm = useCallback(() => {
    const values = getValues();
    return {
      name: values.fullName,
      phone: values.mobile,
      countryCode: values.countryCode,
      email: values.email,
      consent: true,
    };
  }, [getValues]);

  const buildDegreeForm = useCallback(() => {
    const selectedProgram: any =
      selectedProgramIndex >= 0
        ? programs?.[selectedProgramIndex]
        : programs?.[0];
    const programTitle =
      selectedProgram?.attributes?.BannerSlider?.Heading?.text ||
      selectedProgram?.attributes?.BasicDetails?.Heading ||
      "";
    const degree = selectedProgram?.attributes?.BasicDetails?.ProgramType || "";
    return {
      ...buildBaseForm(),
      program: programTitle,
      PROGRAM_ID: selectedDegreeProgramCode || "",
      Custom6: selectedSpecProgramCode,
      degree,
      Custom5: !isInternational && "True",
    };
  }, [
    buildBaseForm,
    isInternational,
    programs,
    selectedDegreeProgramCode,
    selectedProgramIndex,
    selectedSpecProgramCode,
  ]);

  const buildCertForm = useCallback(() => {
    const selectedCert: any =
      selectedCertIndex >= 0
        ? certificate?.[selectedCertIndex]
        : certificate?.[0];
    const certTitle = selectedCert?.attributes?.BasicDetails?.Heading || "";
    const values = getValues();
    const isIndia = values.countryCode === NATIONAL_CODE;
    return {
      ...buildBaseForm(),
      program: certTitle,
      PROGRAM_ID: selectedCertProgramCode || "",
      degree: "",
      Lead_Source: "Website",
      odl_iCurrencyTypeID: isIndia ? "1" : "2",
      Custom5: !isInternational && "True",
    };
  }, [
    buildBaseForm,
    certificate,
    getValues,
    isInternational,
    selectedCertIndex,
    selectedCertProgramCode,
  ]);

  const onValidSubmit = useCallback(async () => {
    setOtpError("");
    try {
      if (!shouldSkipOtp && !isInternational) {
        const verifyData = await verifyLocalOtp({
          hash: otpResponse?.hash,
          mobile: otpResponse?.mobile,
          otp: otp.join(""),
        });
        if (!verifyData?.success) {
          setOtpError(
            verifyData?.message || "Incorrect OTP. Please try again.",
          );
          return;
        }
      }

      const values = getValues();
      const resp: any = await submitProgramSelectorLead(
        { ...buildBaseForm() },
        !isInternational && { Custom5: "True" },
      );
      setLeadResponse(resp);
      pushToDataLayer({
        event: "form_otp_verify",
        cta_text: "Verify OTP",
        lead_starting_cta: "NA",
        lead_starting_url: window.location.href || "NA",
        lead_id: resp?.ProspectId || "NA",
        section_name: getGlobalStateValue("parentSection") || "NA",
        status: "success",
        error_message: "NA",
        page_type: getGlobalStateValue("pageType") || "Home Page",
        page_url: window?.location?.href || "NA",
      });

      pushToDataLayer({
        event: "lead_submit",
        cta_text: "View Recommended Results",
        lead_starting_cta: "Find Your Program",
        lead_starting_url: window.location.href || "NA",
        lead_id: resp?.ProspectId || "NA",
        section_name: "middle_banner",
        status: "success",
        error_message: "NA",
        lead_type: "lead_submit_1",
        page_type: getGlobalStateValue("pageType"),
        email_id: values.email,
        page_url: window?.location?.href || "NA",
        page_url_custom: window?.location?.href || "NA",
      });
      setStep("results");
    } catch {
      setOtpError("Something went wrong. Please try again.");
    }
  }, [
    buildBaseForm,
    getValues,
    isInternational,
    otp,
    otpResponse,
    shouldSkipOtp,
  ]);

  const handleFormSubmit = useCallback(
    async (e: FormEvent) => {
      e?.preventDefault?.();
      if (!canSubmitForm) return;

      try {
        await import("@/helpers/safeSubmit").then((m) =>
          m.submitAfterValidation({
            rhfHandleSubmit,
            startBusy: () => setIsSubmitUIBusy(true),
            onValid: async () => {
              await onValidSubmit();
            },
            onInvalid: () => setIsSubmitUIBusy(false),
            event: e,
          }),
        );
      } catch {
        setIsSubmitUIBusy(false);
      }
    },
    [canSubmitForm, onValidSubmit, rhfHandleSubmit],
  );

  const selectProgram = useCallback((program: any, index: number) => {
    const code = program?.attributes?.BasicDetails?.PROGRAM_ID || "";
    const specilizationCode =
      program?.attributes?.BasicDetails?.SpecializationText || "";
    if (selectedProgramIndex === index) {
      setSelectedProgramIndex(-1);
      setSelectedDegreeProgramCode("");
      return;
    }
    setSelectedProgramIndex(index);
    setSelectedDegreeProgramCode(code);
    setSelectedSpecProgramCode(specilizationCode);
  }, [selectedProgramIndex]);

  const selectCertificate = useCallback(
    (cert: any, index: number) => {
      const code = cert?.attributes?.BasicDetails?.PROGRAM_ID || "";
      if (selectedCertIndex === index) {
        setSelectedCertIndex(-1);
        setSelectedCertProgramCode("");
        return;
      }
      setSelectedCertIndex(index);
      setSelectedCertProgramCode(code);
    },
    [selectedCertIndex],
  );

  const handleProceed = useCallback(async (e?: ReactMouseEvent) => {
    if (isProceeding) return;
    if (!isProgramSelected) {
      setProceedError("Please select a program to proceed.");
      return;
    }
    setProceedError("");
    let endDomBusy: (() => void) | null = null;
    try {
      // Scope paint-first busy to the element that initiated the action.
      // Use the React MouseEvent.currentTarget (the actual button) when available.
      const initiator = (e?.currentTarget as HTMLElement) || undefined;
      endDomBusy = await beginSubmitInteraction(() => setIsProceeding(true), initiator);
      let url = "";
      let resp: any = null;
      if (isCertSelected) {
        resp = await proceedProgramSelectorLead({
          degreeForm: {
            ...buildDegreeForm(),
            ProspectId: leadResponse?.ProspectId || "",
            ContactId: leadResponse?.ContactId || "",
          },
          certForm: buildCertForm(),
          withCert: true,
        });
        url = resp?.degree?.URL || leadResponse?.URL || "";
      } else {
        resp = await proceedProgramSelectorLead({
          degreeForm: {
            ...buildDegreeForm(),
            ProspectId: leadResponse?.ProspectId || "",
            ContactId: leadResponse?.ContactId || "",
          },
          withCert: false,
        });
        url = resp?.URL || leadResponse?.URL || "";
      }
      pushToDataLayer({
        event: "proceed_to_application",
        cta_text: "Proceed",
        lead_starting_cta: "Find Your Program",
        lead_starting_url: window.location.href || "NA",
        lead_id: leadResponse?.ProspectId || "NA",
        section_name: "middle_banner",
        status: "success",
        error_message: "NA",
        page_type: getGlobalStateValue("pageType"),
        page_url: window?.location?.href || "NA",
        page_url_custom: window?.location?.href || "NA",
      });
      if (url) {
        window.location.href = url;
        closeModal();
      } else {
        setProceedError("Unable to proceed. Please try again.");
        setIsProceeding(false);
      }
    } catch {
      setProceedError("Unable to proceed. Please try again.");
      setIsProceeding(false);
    } finally {
      try {
        endDomBusy?.();
      } catch {}
    }
  }, [
    buildCertForm,
    buildDegreeForm,
    closeModal,
    isCertSelected,
    isProceeding,
    isProgramSelected,
    leadResponse,
  ]);

  const handleSetOtp = useCallback((value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;
    setOtp((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  }, []);

  const handleOtpKeyDown = useCallback(
    (e: ReactKeyboardEvent, index: number) => {
      if (e.key === "Backspace" && !otp[index] && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    },
    [otp],
  );

  const handleOtpPaste = useCallback((e: ReactClipboardEvent) => {
    e?.preventDefault?.();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    setOtp((prev) => {
      const next = [...prev];
      pasted.split("").forEach((char, i) => {
        next[i] = char;
      });
      return next;
    });
    const nextIndex = Math.min(pasted.length, 5);
    inputsRef.current[nextIndex]?.focus();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);

    const scrollY = window.scrollY;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyLeft = document.body.style.left;
    const prevBodyRight = document.body.style.right;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      const prevScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "auto";
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.left = prevBodyLeft;
      document.body.style.right = prevBodyRight;
      document.documentElement.style.overflow = prevHtmlOverflow;
      window.scrollTo(0, scrollY);
      document.documentElement.style.scrollBehavior = prevScrollBehavior;
    };
  }, [isOpen, closeModal]);

  return {
    isOpen,
    step,
    questionIndex,
    answers,
    programs,
    certificate,
    control,
    errors,
    otp,
    showOtp,
    otpResponse,
    otpError,
    isSendingOtp,
    resendCooldown,
    isSubmittingForm,
    isProceeding,
    proceedError,
    selectedProgramIndex,
    selectedCertIndex,
    totalQuestions,
    currentQuestion,
    currentOptions,
    questionsProgressPct,
    isInternational,
    isFormValid,
    canSubmitForm,
    canProceed,
    isNational,
    otpDisplayCountryCode: watchedCountryCode || "",
    otpDisplayMobile: watchedMobile || "",
    inputsRef,
    openModal,
    closeModal,
    handleSelectAnswer,
    handleGoBack,
    handleFieldChange,
    handleFieldBlur,
    updateCountryCode,
    handleValidate,
    handleEditNumber,
    handleResendOtp,
    formatCooldown,
    handleFormSubmit,
    selectProgram,
    selectCertificate,
    handleProceed,
    handleSetOtp,
    handleOtpKeyDown,
    handleOtpPaste,
  };
}
